import { useState, useEffect, useRef, FormEvent, KeyboardEvent } from 'react';
import { createFilterOptions, updateActiveTask, Util } from '../../util/Util';
import { Item } from '../../util/Item';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons';
import RefreshButton from '../../components/RefreshButton';
import { SelectInstance } from 'react-select';
import { useOffer } from './OfferContext';
import { useAppSocket } from '../../providers/AppSocketContext';
import { useUI } from '../../providers/MoleUIContext';
import { useAuth } from '../../hooks/useAuth';
import { MYSQL_LIMITS } from '../../util/constants';
import { MONEY_LIMITS, QTY_LIMITS, isWholeNumber, qtyMin } from '../../util/quantity';
import { Autocomplete, Button, Checkbox, Grid, TextField } from '@mui/material';
import QuantityPackHelper from '../../components/QuantityPackHelper';
import { focusAndSelect, isPlainEnter } from '../../util/focusField';
import AsynchronousPrice from '../../components/PriceAutoComplete';
import { renderCatalogItemOption } from '../../components/itemAutocompleteRenderOption';
import {
	catalogItemAutocompleteInteractionProps,
	handleCatalogItemAutocompleteChange,
	isCatalogItemOptionEqualToValue,
} from '../../components/catalogItemAutocomplete';
import { applySavedCatalogOption, CatalogItemPickerProvider } from '../../components/CatalogItemActions';
import type { CatalogItemRow } from '../inventory/ItemDetailModal';
import { Add } from '@mui/icons-material';
import { toast } from 'react-toastify';
import VirtualizedListbox from '../../components/VirtualizedListbox';

import { roundQty } from "../../util/quantity";
interface Props {
	locked?: boolean;
}

interface OptionType {
	value: number;
	label: string;
}

const OfferItemDetail = ({ locked = false }: Props) => {
	const [barcodes, setBarcodes] = useState<any>({});
	const [selectedItem, setSelectedItem] = useState<Item | null>(null);
	const [errorInput, setErrorInput] = useState('');
	const [isItemLoading, setIsItemLoading] = useState(false);
	const [isPriceLoading, setIsPriceLoading] = useState(false);
	const [prices, setPrices] = useState<any[]>([]);
	const [useBarcode, setUseBarcode] = useState(false);
	const [barcode, setBarcode] = useState('');
	const { setPendingTasks, activeTask, items, setItems, apiRef } = useOffer();
	const { queueReRun, t } = useUI();
	const { appSettings } = useAppSocket();
	const { api } = useAuth();
	const barcodeRef = useRef<HTMLInputElement>(null);
	const itemRef = useRef<SelectInstance<OptionType, false>>(null);
	const quantityRef = useRef<HTMLInputElement>(null);
	const priceRef = useRef<HTMLInputElement>(null);
	const errorMessageRef = useRef<string>('');
	const toastIdRef = useRef<string | number>(-1);
	const disabled = !activeTask || locked;

	const fetchItems = async () => {
		setIsItemLoading(true);
		const res = await api.get('items', { params: { Metered: false } });
		if (res?.status === 200) {
			setItems(res.data.items || []);
			setBarcodes(res.data.barcodes || {});
		} else if (res?.status === 401 || res?.status === 422) {
			queueReRun(fetchItems);
		}
		setIsItemLoading(false);
	};

	const fetchPrices = async () => {
		setPrices([]);
		if ((selectedItem?.id || -1) < 1) return;
		setIsPriceLoading(true);
		const res = await api.get(`items/${selectedItem?.id}/price_history`);
		if (res?.status === 200) {
			setPrices(res.data);
		} else if (res?.status === 401 || res?.status === 422) {
			queueReRun(fetchPrices);
		}
		setIsPriceLoading(false);
	};

	const handleCatalogSaved = (updated: CatalogItemRow) => {
		setItems(applySavedCatalogOption(items, updated));
		setSelectedItem((prev) =>
			prev && prev.id === updated.id
				? new Item({
					...prev,
					item: updated.ItemName || prev.item,
					item_code: updated.ItemCode || prev.item_code,
					isFractional: updated.IsFractional === true,
				})
				: prev
		);
		updateActiveTask(setPendingTasks, (prev) => ({
			...prev,
			items: prev.items?.map((it) =>
				it.id === updated.id
					? new Item({
						...it,
						item: updated.ItemName || it.item,
						item_code: updated.ItemCode || it.item_code,
						isFractional: updated.IsFractional === true,
					})
					: it
			),
		}));
	};

	const onItemChanged = (item_id: number) => {
		const item: any = items.find((row: any) => row.id === item_id);
		if (item) {
			setSelectedItem(new Item({
				id: item.id,
				item_code: item.ItemCode,
				item: item.ItemName,
				price: item.SellingPrice ?? item.CostPrice ?? 0,
				stock: item.Stock,
				quantity: 1,
				category: item.Category,
				isFractional: item.IsFractional === true,
			}));
			focusAndSelect(quantityRef);
		} else {
			setSelectedItem(null);
		}
	};

	const lookupBarcode = (code: string = barcode) => {
		const itemID = barcodes[code];
		if (itemID) {
			onItemChanged(itemID);
			focusAndSelect(quantityRef);
		} else {
			itemRef.current?.focus();
			setErrorInput('barcodeInput');
			setTimeout(() => setErrorInput(''), 2000);
		}
	};

	const validateQty = (value: number): boolean => {
		if (value < qtyMin(!!selectedItem?.isFractional) || value > QTY_LIMITS.max) {
			errorMessageRef.current = t('common:validation.quantityRange', { max: Util.formatNumber(QTY_LIMITS.max) });
			setErrorInput('quantityInput');
			setTimeout(() => {
				setErrorInput('');
				errorMessageRef.current = '';
			}, 2000);
			return false;
		}
		if (selectedItem && !selectedItem.isFractional && !isWholeNumber(value)) {
			errorMessageRef.current = t('common:validation.quantityMustBeWhole');
			setErrorInput('quantityInput');
			setTimeout(() => {
				setErrorInput('');
				errorMessageRef.current = '';
			}, 2000);
			return false;
		}
		return true;
	};

	const validateAmt = (value: number): boolean => {
		if (isNaN(value) || value < 0 || value > MONEY_LIMITS.max) {
			setErrorInput('amountInput');
			setTimeout(() => setErrorInput(''), 2000);
			return false;
		}
		return true;
	};

	const onAddClicked = (e: FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		if (locked || !selectedItem || !activeTask) return;
		if (!validateQty(selectedItem.quantity) || !validateAmt(selectedItem.price)) return;

		const existingIndex = activeTask.items!.findIndex((r) => Util.compareStrings(r.uniqueId, selectedItem.uniqueId));
		const mergedRow = new Item({ ...selectedItem });
		if (existingIndex > -1) {
			mergedRow.quantity = roundQty(mergedRow.quantity + activeTask.items![existingIndex].quantity);
			if (apiRef.current) {
				const rowIndex = apiRef.current.getRowIndexRelativeToVisibleRows(mergedRow.uniqueId) + 1;
				if (mergedRow.quantity > QTY_LIMITS.max) {
					errorMessageRef.current = t('common:validation.mergeExceedsLimit', { row: rowIndex, max: Util.formatNumber(QTY_LIMITS.max) });
					setErrorInput('quantityInput');
					setTimeout(() => {
						setErrorInput('');
						errorMessageRef.current = '';
					}, 5000);
					return;
				}
				const msg = t('common:info.mergedWithRow', { row: rowIndex, quantity: mergedRow.quantity });
				if (!toast.isActive(toastIdRef.current)) {
					toastIdRef.current = toast.info(msg, { autoClose: 5000 });
				} else {
					toast.update(toastIdRef.current, { render: msg, autoClose: 5000 });
				}
			}
		}

		updateActiveTask(setPendingTasks, (prev) => ({
			...prev,
			items: existingIndex > -1
				? prev.items!.map((item, idx) => (idx === existingIndex ? mergedRow : item))
				: [...prev.items!, mergedRow],
		}));

		focusAndSelect(useBarcode ? barcodeRef : itemRef);
	};

	useEffect(() => {
		void fetchItems();
	}, []);

	useEffect(() => {
		focusAndSelect(useBarcode ? barcodeRef : itemRef);
	}, [useBarcode]);

	useEffect(() => {
		setUseBarcode(Util.isBoolSettingEnabled(appSettings?.UI_ENABLE_BARCODE?.value, true));
	}, []);

	// Enter in quantity moves to the price; Enter there adds the line (form submit).
	const onQuantityKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
		if (!isPlainEnter(event)) return;
		event.preventDefault();
		focusAndSelect(priceRef);
	};

	return (
		<form onSubmit={onAddClicked} style={{ marginBottom: '10px' }}>
			<Grid container rowSpacing={0} columnSpacing={0}>
				<Grid size={{ xs: 12, sm: 6, md: 2 }}>
					<TextField
						id="barcodeInput"
						size="small"
						label={
							<span>
								{t('common:ui.labels.barcode')}&nbsp;&nbsp;
								<Checkbox checked={useBarcode} onChange={() => setUseBarcode(!useBarcode)} disabled={disabled} />
							</span>
						}
						variant="filled"
						fullWidth
						type="text"
						value={barcode}
						inputProps={{ maxLength: MYSQL_LIMITS.VARCHAR(50).maxLength }}
						inputRef={barcodeRef}
						slotProps={{
							input: {
								endAdornment: <FontAwesomeIcon icon={faMagnifyingGlass} onClick={() => lookupBarcode()} />,
							},
						}}
						onFocus={(e) => e.target.select()}
						onKeyDown={(e) => {
							if (e.key === 'Enter') {
								e.preventDefault();
								lookupBarcode(barcode);
							}
						}}
						onChange={(e) => setBarcode(e.target.value)}
						disabled={disabled || isItemLoading || Object.keys(barcodes).length === 0}
						error={errorInput === 'barcodeInput'}
						helperText={errorInput === 'barcodeInput' ? t('common:stockTrace.barcodeNotFound') : ''}
					/>
				</Grid>
				<Grid size={{ xs: 12, sm: 6, md: 4 }}>
					<CatalogItemPickerProvider onItemSaved={handleCatalogSaved}>
						<Autocomplete
							id="itemInput"
							size="small"
							options={items}
							ListboxComponent={VirtualizedListbox}
							filterOptions={createFilterOptions((opt) => `${opt.ItemName || ''} ${opt.ItemCode || ''}`)}
							{...catalogItemAutocompleteInteractionProps}
							getOptionLabel={(item) => item.ItemName || ''}
							isOptionEqualToValue={isCatalogItemOptionEqualToValue}
							renderOption={(props, option) => renderCatalogItemOption(props, option, 'both')}
							value={items.find((i) => i.id === selectedItem?.id) || null}
							onChange={(_, newValue, reason) => {
								handleCatalogItemAutocompleteChange(reason, newValue, {
									onSelect: (id) => onItemChanged(id),
									onClear: () => onItemChanged(-1),
								});
							}}
							loading={isItemLoading}
							disabled={disabled}
							renderInput={(params) => (
								<TextField
									{...params}
									label={
										<span>
											{t('common:ui.labels.item')}&nbsp;&nbsp;
											<RefreshButton isWorking={isItemLoading} onClick={fetchItems} disabled={isItemLoading} />
										</span>
									}
									placeholder={isItemLoading ? t('common:ui.placeholders.loadingItems') : t('common:ui.placeholders.selectItem')}
									required
									onFocus={(e) => e.target.select()}
									inputRef={itemRef}
									variant="filled"
									fullWidth
								/>
							)}
						/>
					</CatalogItemPickerProvider>
				</Grid>
				<Grid size={{ xs: 6, sm: 6, md: 2 }}>
					<QuantityPackHelper
						id="quantityInput"
						size="small"
						label={t('common:ui.labels.quantity')}
						fullWidth
						required
						disabled={!selectedItem || disabled}
						value={selectedItem?.quantity ?? 1}
						min={qtyMin(!!selectedItem?.isFractional)}
						fractional={selectedItem?.isFractional}
						inputRef={quantityRef}
						onKeyDown={onQuantityKeyDown}
						onChange={(newValue) => {
							if (validateQty(newValue)) {
								setSelectedItem((prev) => prev ? new Item({ ...prev, quantity: newValue }) : null);
							}
						}}
						error={errorInput === 'quantityInput'}
						helperText={errorInput === 'quantityInput' ? errorMessageRef.current : undefined}
					/>
				</Grid>
				<Grid size={{ xs: 6, sm: 6, md: 2 }}>
					<AsynchronousPrice
						fetchPrices={fetchPrices}
						prices={prices}
						selectedItem={selectedItem}
						setSelectedItem={setSelectedItem}
						itemConstructor={Item}
						isPriceLoading={isPriceLoading}
						inputRef={priceRef}
					/>
				</Grid>
				<Grid size={{ xs: 12, sm: 12, md: 2 }} display="flex" justifyContent="center" alignItems="start">
					<Button fullWidth size="large" type="submit" color="success" startIcon={<Add />} sx={{ fontWeight: 'bolder' }} disabled={disabled}>
						{t('common:ui.buttons.add')}
					</Button>
				</Grid>
			</Grid>
		</form>
	);
};

export default OfferItemDetail;
